import { Button, Grid } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAtom, useSetAtom } from 'jotai';
import { FormProvider, useForm } from 'react-hook-form';
import { Duration_Type_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { RoutePaths } from '../../../routes/AppRoutes';
import { DraftProduct, draftProductAtom, productSubmittedAtom } from '../state';
import InvalidFormBanner from '../InvalidFormBanner';
import useOpenable from '../../../hooks/useOpenable';
import ProductFormQuitBtn from '../ProductFormQuitBtn';
import ProductFormSpecificationsFields from './ProductFormSpecificationsFields';

export default function ProductFormSpecifications(): React.JSX.Element {
  const form = useForm<DraftProduct>({
    defaultValues: {
      name: '',
      sub_family: '' as Product_Sub_Family_Name_Enum,
      labels: [],
      vat_rate: 0,
      ingredients: '',
      shelf_life_duration_type: Duration_Type_Enum.Days,
      allergens: '',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      calories: '' as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      calories_kj: '' as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fat: '' as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      saturated_fat: '' as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      carbohydrate: '' as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sugars: '' as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sodium: '' as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      protein: '' as any,
      description: '',
      preservation_method: null,
    },
  });
  const {
    control,
    handleSubmit,
    formState: {
      submitCount,
      isValid,
      isDirty,
      isSubmitSuccessful,
    },
    setValue,
    reset,
  } = form;
  const { t } = useTranslation(['common', 'products', 'validation']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [draftProduct, setDraftProduct] = useAtom(draftProductAtom);
  const {
    isOpen: isInvalidFormBannerOpen,
    open: openInvalidFormBanner,
    close: closeInvalidFormBanner,
  } = useOpenable();
  const setProductSubmitted = useSetAtom(productSubmittedAtom);
  const productId = searchParams.get('productId');

  const onSubmit = (data: DraftProduct): void => {
    setDraftProduct(data);

    if (productId) {
      setProductSubmitted(true);
      reset(undefined, { keepValues: true });
    }
  };

  useEffect(() => {
    if (!draftProduct) {
      navigate(RoutePaths.PRODUCT_ADD_INFO, { replace: true });
    }
  }, [draftProduct, navigate]);

  useEffect(() => {
    if (isSubmitSuccessful && !productId) {
      navigate({
        pathname: RoutePaths.PRODUCT_ADD_PACKAGING,
        search: `?${searchParams.toString()}`,
      });
    }
  }, [isSubmitSuccessful, productId, navigate, searchParams]);

  useEffect(() => {
    if (submitCount && !isValid) {
      openInvalidFormBanner();
    }
  }, [submitCount, isValid, openInvalidFormBanner]);

  useEffect(() => {
    if (draftProduct) {
      Object.entries(draftProduct).forEach(([key, value]) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValue(key as any, value);
      });
      reset(undefined, { keepValues: true });
    }
  }, [draftProduct, setValue, reset]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container rowSpacing={2}>
          <ProductFormSpecificationsFields control={control} />

          {isInvalidFormBannerOpen && (
            <Grid
              size={{ xs: 12 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 2,
              }}
            >
              <InvalidFormBanner close={closeInvalidFormBanner} message={t('validation:fill_the_marked_field_to_continue')} />
            </Grid>
          )}
          <Grid
            size={{ xs: 12 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              gap: { xs: 1, sm: 2 },
              mt: { xs: 0, sm: 2 },
            }}
          >
            <ProductFormQuitBtn productId={productId || undefined} shouldBlock={isDirty && !isSubmitSuccessful} />
            {!productId && (
              <Button
                variant="outlined"
                component={Link}
                to={{
                  pathname: RoutePaths.PRODUCT_ADD_INFO,
                  search: `?${searchParams.toString()}`,
                }}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {t('common:previous')}
              </Button>
            )}
            <Button
              variant="contained"
              type="submit"
              disabled={!!productId && !isDirty}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {productId ? t('common:validate') : t('common:next')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}
